import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { PropsWithChildren } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { usePostHog } from "posthog-js/react";
import { supabase } from "@/lib/supabase";
import type { Database } from "@/lib/database.types";
import { updatePreferences } from "@/lib/api/preferences";
import { setMySubjects } from "@/lib/my-subjects";
import { AuthContext } from "./AuthContext";
import type { Profile } from "./AuthContext";

export type { AuthContextValue, Profile } from "./AuthContext";
export { AuthContext } from "./AuthContext";

async function upsertProfile(user: User, displayName?: string | null) {
  const payload: Database["public"]["Tables"]["profiles"]["Insert"] = {
    id: user.id,
    display_name: displayName ?? user.user_metadata?.full_name ?? null,
    avatar_url: user.user_metadata?.avatar_url ?? null,
    updated_at: new Date().toISOString(),
  };
  const { error } = await supabase.from("profiles").upsert(payload);
  if (error) {
    throw error;
  }
}

export function AuthProvider({ children }: PropsWithChildren) {
  const mountedRef = useRef(true);
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const posthog = usePostHog();

  // Track user identification changes
  const prevUserIdRef = useRef<string | null>(null);

  // Identify user in PostHog when they log in
  useEffect(() => {
    if (!posthog) return;
    
    const currentUserId = user?.id ?? null;
    const prevUserId = prevUserIdRef.current;
    
    // User logged in (was null, now has value)
    if (currentUserId && !prevUserId) {
      posthog.identify(currentUserId, {
        email: user?.email,
        name: user?.user_metadata?.full_name,
        createdAt: user?.created_at,
      });
    }
    
    // User logged out (had value, now null)
    if (!currentUserId && prevUserId) {
      posthog.reset();
    }
    
    prevUserIdRef.current = currentUserId;
  }, [user, posthog]);

  const loadProfile = useCallback(async (nextUser: User | null) => {
    if (!mountedRef.current) return;
    if (!nextUser) {
      setProfile(null);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", nextUser.id)
        .maybeSingle();

      if (error) {
        throw error;
      }

      if (!data) {
        await upsertProfile(nextUser, nextUser.user_metadata?.full_name ?? null);
        const { data: created, error: fetchError } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", nextUser.id)
          .maybeSingle();

        if (fetchError) {
          throw fetchError;
        }

        if (!mountedRef.current) return;
        setProfile(created ?? null);
        return;
      }

      if (!mountedRef.current) return;
      setProfile(data);
    } catch (error) {
      console.error("Failed to load profile", error);
      if (mountedRef.current) {
        setProfile(null);
      }
    }
  }, [mountedRef]);

  useEffect(() => {
    mountedRef.current = true;
    let ignore = false;
    (async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          throw error;
        }
        if (ignore) return;
        const nextSession = data.session ?? null;
        setSession(nextSession);
        setUser(nextSession?.user ?? null);
        setLoading(false);
        void loadProfile(nextSession?.user ?? null);
      } catch (error) {
        console.error("Failed to initialize auth session", error);
        if (!ignore) {
          setSession(null);
          setUser(null);
          setProfile(null);
          setLoading(false);
        }
      }
    })();
    const { data: listener } = supabase.auth.onAuthStateChange((_event, updatedSession) => {
      if (ignore) return;
      setSession(updatedSession);
      setUser(updatedSession?.user ?? null);
      void loadProfile(updatedSession?.user ?? null);
      setLoading(false);
    });
    return () => {
      ignore = true;
      mountedRef.current = false;
      listener?.subscription?.unsubscribe();
    };
  }, [loadProfile]);

  const signIn = useCallback(async ({ email, password }: { email: string; password: string }) => {
    try {
      const { error, data } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        return { error: error.message };
      }

      const signedInUser = data.session?.user ?? null;
      if (signedInUser) {
        try {
          await upsertProfile(signedInUser, signedInUser.user_metadata?.full_name ?? null);
        } catch (profileError) {
          console.error("Failed to upsert profile after sign in", profileError);
        }
        await loadProfile(signedInUser);
        
        // Track login event
        posthog?.capture("user_logged_in", { method: "email" });
      }
      return {};
    } catch (error) {
      console.error("Failed to sign in", error);
      return { error: error instanceof Error ? error.message : "Unable to sign in right now." };
    }
  }, [loadProfile, posthog]);

  const signInWithOAuth = useCallback(async (provider: string) => {
    try {
      // Track OAuth sign-in attempt (we'll track the actual login via onAuthStateChange)
      posthog?.capture("oauth_sign_in_initiated", { provider });
      
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Supabase accepts string providers
      const { error } = await supabase.auth.signInWithOAuth({ provider: provider as any });
      if (error) return { error: error.message };
      return {};
    } catch (err) {
      console.error("Failed to start OAuth sign in", err);
      return { error: err instanceof Error ? err.message : "Unable to start OAuth sign in." };
    }
  }, [posthog]);

  const resetPassword = useCallback(async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth?mode=update-password`,
      });
      if (error) {
        return { error: error.message };
      }
      return {};
    } catch (error) {
      console.error("Failed to send password reset email", error);
      return { error: error instanceof Error ? error.message : "Unable to send password reset email." };
    }
  }, []);

  const updatePassword = useCallback(async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) {
        return { error: error.message };
      }
      return {};
    } catch (error) {
      console.error("Failed to update password", error);
      return { error: error instanceof Error ? error.message : "Unable to update password." };
    }
  }, []);

  const signOut = useCallback(async () => {
    // Track logout event before signing out
    posthog?.capture("user_logged_out");
    
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error("Failed to sign out", error);
    }
  }, [posthog]);

  const refreshProfile = useCallback(async () => {
    await loadProfile(user);
  }, [loadProfile, user]);

  const value = useMemo<AuthContextValue>(() => ({
    session,
    user,
    profile,
    loading,
    signIn,
    signInWithOAuth,
    resetPassword,
    updatePassword,
    signOut,
    refreshProfile,
  }), [session, user, profile, loading, signIn, signInWithOAuth, resetPassword, updatePassword, signOut, refreshProfile]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
